// src/pages/home/components/ListCard.tsx
import React from "react";
import WalkPng from "../../../styles/assets/img/walk.png";
import DietPng from "../../../styles/assets/img/diet.png";
import PillPng from "../../../styles/assets/img/pill.png";
import dayjs from "dayjs";
import { useMedRegister } from "src/common/hooks/usePillRegister";
import { useDietRegister } from "src/common/hooks/useDietRegister";
import { useQuery } from "@tanstack/react-query";
import { GET_WALK } from "src/common/querykeys";
import { request } from "src/common/api";
import { BaseResponse } from "src/common/types/baseRespones";

type Props = {
  width?: number; // 기본 328
  onRowClick?: (id: string) => void;
};

const Chevron = ({ size = 18, color = "#C7CCD3" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M9 18l6-6-6-6"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/* 제일 빠른 복약 예정 시간 가져오기 */
const getNextPillTime = (pillData: any[]) => {
  const now = dayjs();

  const candidates = pillData.flatMap((pill) => {
    const historiesTimes = pill.histories?.map((h: any) => h.mediTime) || [];

    return (pill.timeList || [])
      .filter((t: string) => !historiesTimes.includes(t))
      .map((t: string) =>
        dayjs(`${now.format("YYYY-MM-DD")} ${t}`, "YYYY-MM-DD HH:mm")
      );
  });

  // ✅ 모든 약의 timeList와 histories가 동일할 때 → 복용 완료
  const allTaken = pillData.every(
    (pill) =>
      pill.timeList?.length > 0 &&
      pill.histories?.length === pill.timeList.length
  );

  if (allTaken) return { time: null, status: "done" };

  if (candidates.length === 0) return { time: null, status: "missed" };

  const nextTime = candidates.sort((a, b) => a.valueOf() - b.valueOf())[0];
  const isPast = nextTime.isBefore(now);

  if (isPast) return { time: nextTime.format("HH:mm"), status: "missed" };
  return { time: nextTime.format("HH:mm"), status: "upcoming" };
};

const ListCard: React.FC<Props> = ({ onRowClick }) => {
  const today = dayjs().format("YYYYMMDD");

  const { data: dietData = [] } = useDietRegister(today);

  const { data: pillData = [] } = useMedRegister(today);

  // 해당 날짜 걷기 데이터 불러오기
  const useWalkRegister = (date?: string) => {
    return useQuery({
      queryKey: [GET_WALK, date],
      queryFn: async () => {
        const result = await request<BaseResponse<any>>({
          method: "GET",
          url: `/health-steps?date=${date}`,
        });
        return result.response || [];
      },
    });
  };
  const { data: walkData = [] } = useWalkRegister(today);

  const totalCalorie = dietData.reduce(
    (sum, item) => sum + (item.totalCalorie || 0),
    0
  );

  const pillItem = pillData.filter((pill) => pill.filtered === true);
  const nextPillTime = getNextPillTime(pillItem);

  const rows = [
    {
      id: "diet",
      title: "식단",
      value: dietData.length > 0 ? totalCalorie.toLocaleString() : "기록 없음",
      sub: dietData.length > 0 ? "칼로리 섭취" : "",
      iconBg: "#F3F3F3",
      iconSrc: DietPng,
    },
    {
      id: "pill",
      title: "복약",
      value:
        pillData.length === 0
          ? "기록 없음"
          : pillItem.length === 0
          ? "복용할 약이 없어요"
          : nextPillTime?.status === "done"
          ? "복용 완료"
          : nextPillTime?.time,
      sub:
        nextPillTime?.status === "upcoming"
          ? "복용 예정"
          : nextPillTime?.status === "missed"
          ? "복용하지 않았습니다"
          : "",
      iconBg: "#EFF8F5",
      iconSrc: PillPng,
    },
    {
      id: "walk",
      title: "걷기",
      value:
        walkData.length === 0
          ? "0"
          : Number(walkData[0]?.stepCount).toLocaleString(),
      sub: "걷는 중",
      iconBg: "#E9F1FF",
      iconSrc: WalkPng,
    },
    // {
    //   id: "workout",
    //   title: "운동",
    //   value: "149",
    //   sub: "칼로리 소모",
    //   iconBg: "#FFF3E8",
    //   iconSrc: WorkoutPng,
    // },
  ] as const;

  return (
    <section
      style={{
        width: "100%",
        background: "#FFFFFF",
        borderRadius: 16,
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
      }}
    >
      {rows.map((r, i) => {
        const Last = i === rows.length - 1;
        return (
          <div key={r.id}>
            <button
              onClick={() => onRowClick?.(r.id)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "14px 16px",
                background: "transparent",
                border: 0,
                cursor: onRowClick ? "pointer" : "default",
              }}
            >
              {/* 아이콘 */}
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: r.iconBg,
                  display: "grid",
                  placeItems: "center",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
                  flex: "0 0 44px",
                }}
              >
                <img
                  src={r.iconSrc}
                  alt={r.title}
                  width={32}
                  height={32}
                  style={{ display: "block" }}
                />
              </div>

              {/* 텍스트 영역: 2줄 레이아웃 */}
              <div style={{ flex: 1, minWidth: 0, display: "grid", rowGap: 4 }}>
                {/* 1줄: 제목 */}
                <div
                  style={{
                    fontSize: 16,
                    textAlign: "left",
                    fontWeight: 500,
                    color: "#A3A3A3",
                  }}
                >
                  {r.title}
                </div>
                {/* 2줄: 값 + 서브텍스트 */}
                <div
                  style={{ display: "flex", alignItems: "baseline", gap: 8 }}
                >
                  <span
                    style={{ fontSize: 16, fontWeight: 700, color: "#404040" }}
                  >
                    {r.value}
                  </span>
                  <span style={{ fontSize: 12, color: "#9CA3AF" }}>
                    {r.sub}
                  </span>
                </div>
              </div>

              <Chevron />
            </button>

            {!Last && (
              <div
                style={{ margin: "0 16px", borderBottom: "1px dashed #ECEEF2" }}
              />
            )}
          </div>
        );
      })}
    </section>
  );
};

export default ListCard;
